import { createReducer, createSelector } from '@reduxjs/toolkit';

import type {
	DuelHistoryEntryDto,
	DuelHistoryResponse,
	DuelHubResponse,
	DuelPlayerRewardDto,
	DuelPlayerSeasonDto,
	DuelResultResponse,
	DuelRewardStatus,
	DuelSeasonLeaderboardResponse,
	DuelSeasonProgressResponse,
	DuelSnapshotDto,
} from '../../services/api';
import { loginSuccess, logout } from '../auth/authActions';
import {
	duelResultFailure,
	duelResultFocus,
	duelResultSuccess,
	duelRewardCancelSubmit,
	duelRewardClaimErrorCleared,
	duelRewardClaimFailure,
	duelRewardClaimSubmit,
	duelRewardRequestSubmit,
	duelRewardTransitionFailure,
	duelRewardTransitionReset,
	duelRewardTransitionSuccess,
	duelsConnectionChanged,
	duelsCurrentCleared,
	duelsCurrentReady,
	duelsHistoryFailure,
	duelsHistoryLoadMore,
	duelsHistoryRequest,
	duelsHistorySuccess,
	duelsHubFailure,
	duelsHubRequest,
	duelsHubSuccess,
	duelsJoinErrorDismiss,
	duelsJoinFailure,
	duelsJoinRequest,
	duelsJoinSuccess,
	duelsPlayResume,
	duelsPlayStop,
	duelsRankingFailure,
	duelsRankingRequest,
	duelsRankingSuccess,
	duelsResultOpened,
	duelsRewardsFailure,
	duelsRewardsRequest,
	duelsRewardsSuccess,
	duelsSeasonFailure,
	duelsSeasonRequest,
	duelsSeasonsSuccess,
	duelsSeasonSuccess,
	duelsSnapshotReceived,
} from './duelsActions';

export type DuelConnectionState = 'idle' | 'connecting' | 'connected' | 'unavailable' | 'replaced';

export type DuelHistoryTab = 'all' | 'current' | 'previous';

export type DuelHistoryFilter = { tab: DuelHistoryTab; duelTypeId: string | null };

export type DuelRankingScope = 'current' | 'previous';

export type DuelRewardOverlay = { from: DuelRewardStatus; status: DuelRewardStatus };

export type DuelsState = {
	connection: DuelConnectionState;

	snapshots: Record<string, DuelSnapshotDto>;
	currentDuelId: string | null;
	currentLoaded: boolean;

	joining: boolean;
	joinErrorKey?: string;
	playingDuelId: string | null;
	lastPlayedDuelId: string | null;
	pendingResultDuelId: string | null;

	hub: DuelHubResponse | null;
	hubLoading: boolean;
	hubErrorKey?: string;

	results: Record<string, DuelResultResponse>;
	resultErrorKeys: Record<string, string>;

	history: {
		filter: DuelHistoryFilter;
		items: DuelHistoryEntryDto[];
		personalBests: DuelHistoryResponse['personalBests'];
		nextCursor: string | null;
		loading: boolean;
		loaded: boolean;
		errorKey?: string;
	};

	seasons: DuelPlayerSeasonDto[] | null;

	season: DuelSeasonProgressResponse | null;
	seasonLoading: boolean;
	seasonErrorKey?: string;

	rewards: DuelPlayerRewardDto[] | null;
	rewardsLoading: boolean;
	rewardsErrorKey?: string;
	rewardOverlays: Record<string, DuelRewardOverlay>;
	claimingRewardIds: string[];
	claimErrorKey?: string;

	transition: {
		submitting: boolean;
		errorKey?: string;
		doneId: string | null;
		doneKind: 'request' | 'cancel' | null;
	};

	ranking: Record<
		DuelRankingScope,
		{
			board: DuelSeasonLeaderboardResponse | null;
			loading: boolean;
			loaded: boolean;
			errorKey?: string;
		}
	>;
};

const initialHistory: DuelsState['history'] = {
	filter: { tab: 'all', duelTypeId: null },
	items: [],
	personalBests: [],
	nextCursor: null,
	loading: false,
	loaded: false,
	errorKey: undefined,
};

const initial: DuelsState = {
	connection: 'idle',
	snapshots: {},
	currentDuelId: null,
	currentLoaded: false,
	joining: false,
	joinErrorKey: undefined,
	playingDuelId: null,
	lastPlayedDuelId: null,
	pendingResultDuelId: null,
	hub: null,
	hubLoading: false,
	hubErrorKey: undefined,
	results: {},
	resultErrorKeys: {},
	history: initialHistory,
	seasons: null,
	season: null,
	seasonLoading: false,
	seasonErrorKey: undefined,
	rewards: null,
	rewardsLoading: false,
	rewardsErrorKey: undefined,
	rewardOverlays: {},
	claimingRewardIds: [],
	claimErrorKey: undefined,
	transition: { submitting: false, errorKey: undefined, doneId: null, doneKind: null },
	ranking: {
		current: { board: null, loading: false, loaded: false },
		previous: { board: null, loading: false, loaded: false },
	},
};

const sameFilter = (a: DuelHistoryFilter, b: DuelHistoryFilter) =>
	a.tab === b.tab && a.duelTypeId === b.duelTypeId;

const applySnapshot = (s: DuelsState, snapshot: DuelSnapshotDto) => {
	const previous = s.snapshots[snapshot.id];
	s.snapshots[snapshot.id] = snapshot;

	if (snapshot.status === 'completed') {
		if (s.currentDuelId === snapshot.id) s.currentDuelId = null;
	} else {
		s.currentDuelId = snapshot.id;
	}

	const justFinished = snapshot.currentPlayerFinished && !previous?.currentPlayerFinished;
	const played = s.playingDuelId === snapshot.id || s.lastPlayedDuelId === snapshot.id;
	if (played && justFinished) {
		s.playingDuelId = null;
		s.lastPlayedDuelId = null;
		s.pendingResultDuelId = snapshot.id;
	}
};

export const duelsReducer = createReducer(initial, b => {
	b.addCase(duelsConnectionChanged, (s, a) => {
		s.connection = a.payload;
		if (a.payload !== 'connected') s.currentLoaded = false;
	});

	b.addCase(duelsSnapshotReceived, (s, a) => applySnapshot(s, a.payload));
	b.addCase(duelsCurrentReady, s => {
		s.currentLoaded = true;
	});
	b.addCase(duelsCurrentCleared, s => {
		s.currentDuelId = null;
	});

	b.addCase(duelsJoinRequest, s => {
		s.joining = true;
		s.joinErrorKey = undefined;
	});
	b.addCase(duelsJoinSuccess, (s, a) => {
		s.joining = false;
		s.joinErrorKey = undefined;
		const alreadyFinished = a.payload.currentPlayerFinished;
		applySnapshot(s, a.payload);
		if (alreadyFinished) {
			s.pendingResultDuelId = a.payload.id;
		} else {
			s.playingDuelId = a.payload.id;
			s.lastPlayedDuelId = a.payload.id;
		}
	});
	b.addCase(duelsJoinFailure, (s, a) => {
		s.joining = false;
		s.joinErrorKey = a.payload;
	});
	b.addCase(duelsJoinErrorDismiss, s => {
		s.joinErrorKey = undefined;
	});

	b.addCase(duelsPlayResume, (s, a) => {
		s.playingDuelId = a.payload;
		s.lastPlayedDuelId = a.payload;
	});
	b.addCase(duelsPlayStop, s => {
		s.playingDuelId = null;
	});
	b.addCase(duelsResultOpened, s => {
		s.pendingResultDuelId = null;
	});

	b.addCase(duelsHubRequest, s => {
		s.hubLoading = true;
		s.hubErrorKey = undefined;
	});
	b.addCase(duelsHubSuccess, (s, a) => {
		s.hubLoading = false;
		s.hub = a.payload;
	});
	b.addCase(duelsHubFailure, (s, a) => {
		s.hubLoading = false;
		s.hubErrorKey = a.payload;
	});

	b.addCase(duelResultFocus, (s, a) => {
		delete s.resultErrorKeys[a.payload];
	});
	b.addCase(duelResultSuccess, (s, a) => {
		s.results[a.payload.duelId] = a.payload;
		delete s.resultErrorKeys[a.payload.duelId];
	});
	b.addCase(duelResultFailure, (s, a) => {
		s.resultErrorKeys[a.payload.duelId] = a.payload.key;
	});

	b.addCase(duelsHistoryRequest, (s, a) => {
		s.history = {
			...initialHistory,
			filter: a.payload,
			personalBests: s.history.personalBests,
			loading: true,
		};
	});
	b.addCase(duelsHistoryLoadMore, s => {
		if (s.history.nextCursor) s.history.loading = true;
	});
	b.addCase(duelsHistorySuccess, (s, a) => {
		if (!sameFilter(s.history.filter, a.payload.filter)) return;

		s.history.loading = false;
		s.history.loaded = true;
		s.history.errorKey = undefined;
		s.history.items = a.payload.append
			? [...s.history.items, ...a.payload.page.items]
			: a.payload.page.items;
		s.history.nextCursor = a.payload.page.nextCursor;
		if (a.payload.page.personalBests.length) {
			s.history.personalBests = a.payload.page.personalBests;
		}
	});
	b.addCase(duelsHistoryFailure, (s, a) => {
		s.history.loading = false;
		s.history.errorKey = a.payload;
	});

	b.addCase(duelsSeasonsSuccess, (s, a) => {
		s.seasons = a.payload;
	});

	b.addCase(duelsSeasonRequest, s => {
		s.seasonLoading = true;
		s.seasonErrorKey = undefined;
	});
	b.addCase(duelsSeasonSuccess, (s, a) => {
		s.seasonLoading = false;
		s.season = a.payload;
	});
	b.addCase(duelsSeasonFailure, (s, a) => {
		s.seasonLoading = false;
		s.seasonErrorKey = a.payload;
	});

	b.addCase(duelsRewardsRequest, s => {
		s.rewardsLoading = true;
		s.rewardsErrorKey = undefined;
	});
	b.addCase(duelsRewardsSuccess, (s, a) => {
		s.rewardsLoading = false;
		s.rewards = a.payload;
		for (const reward of a.payload) {
			const overlay = s.rewardOverlays[reward.id];
			const settled =
				overlay &&
				(reward.status === overlay.status ||
					(reward.status !== overlay.from && reward.status !== overlay.status));
			if (settled) delete s.rewardOverlays[reward.id];
		}
	});
	b.addCase(duelsRewardsFailure, (s, a) => {
		s.rewardsLoading = false;
		s.rewardsErrorKey = a.payload;
	});

	b.addCase(duelRewardRequestSubmit, s => {
		s.transition = { submitting: true, errorKey: undefined, doneId: null, doneKind: null };
	});
	b.addCase(duelRewardCancelSubmit, s => {
		s.transition = { submitting: true, errorKey: undefined, doneId: null, doneKind: null };
	});
	b.addCase(duelRewardClaimSubmit, (s, a) => {
		if (!s.claimingRewardIds.includes(a.payload)) s.claimingRewardIds.push(a.payload);
		s.claimErrorKey = undefined;
	});
	b.addCase(duelRewardClaimErrorCleared, s => {
		s.claimErrorKey = undefined;
	});
	b.addCase(duelRewardClaimFailure, (s, a) => {
		s.claimingRewardIds = s.claimingRewardIds.filter(id => id !== a.payload.id);
		s.claimErrorKey = a.payload.key;
	});
	b.addCase(duelRewardTransitionSuccess, (s, a) => {
		s.rewardOverlays[a.payload.id] = { from: a.payload.from, status: a.payload.status };
		if (a.payload.kind === 'claim') {
			s.claimingRewardIds = s.claimingRewardIds.filter(id => id !== a.payload.id);
			return;
		}
		s.transition = {
			submitting: false,
			errorKey: undefined,
			doneId: a.payload.id,
			doneKind: a.payload.kind,
		};
	});
	b.addCase(duelRewardTransitionFailure, (s, a) => {
		s.transition = { submitting: false, errorKey: a.payload, doneId: null, doneKind: null };
	});
	b.addCase(duelRewardTransitionReset, s => {
		s.transition = { submitting: false, errorKey: undefined, doneId: null, doneKind: null };
	});

	b.addCase(duelsRankingRequest, (s, a) => {
		s.ranking[a.payload].loading = true;
		s.ranking[a.payload].errorKey = undefined;
	});
	b.addCase(duelsRankingSuccess, (s, a) => {
		s.ranking[a.payload.scope] = { board: a.payload.board, loading: false, loaded: true };
	});
	b.addCase(duelsRankingFailure, (s, a) => {
		s.ranking[a.payload.scope].loading = false;
		s.ranking[a.payload.scope].errorKey = a.payload.key;
	});

	b.addCase(logout, () => initial);
	b.addCase(loginSuccess, () => initial);
});

const OPEN_REQUEST_STATUSES: DuelRewardStatus[] = ['Requested', 'UnderReview', 'Approved'];

const withOverlay = (
	reward: DuelPlayerRewardDto,
	overlay: DuelRewardOverlay | undefined,
): DuelPlayerRewardDto =>
	overlay
		? {
				...reward,
				status: overlay.status,
				canRequest: false,
				canCancel: overlay.status === 'Requested',
				canClaim: false,
			}
		: reward;

export const isOpenRequest = (reward: DuelPlayerRewardDto) =>
	OPEN_REQUEST_STATUSES.includes(reward.status);

export const isRewardInPlay = (reward: DuelPlayerRewardDto) =>
	reward.canRequest || reward.canCancel || isOpenRequest(reward);

type WithDuels = { duels: DuelsState };

export const selectDuelsConnection = (s: WithDuels) => s.duels.connection;
export const selectDuelSnapshot = (duelId: string | null) => (s: WithDuels) =>
	duelId ? (s.duels.snapshots[duelId] ?? null) : null;
export const selectCurrentDuel = (s: WithDuels) =>
	s.duels.currentDuelId ? (s.duels.snapshots[s.duels.currentDuelId] ?? null) : null;
export const selectCurrentDuelLoaded = (s: WithDuels) => s.duels.currentLoaded;
export const selectDuelsJoining = (s: WithDuels) => s.duels.joining;
export const selectDuelsJoinErrorKey = (s: WithDuels) => s.duels.joinErrorKey;
export const selectPlayingDuelId = (s: WithDuels) => s.duels.playingDuelId;
export const selectPendingResultDuelId = (s: WithDuels) => s.duels.pendingResultDuelId;
export const selectDuelsHub = (s: WithDuels) => s.duels.hub;
export const selectDuelsHubLoading = (s: WithDuels) => s.duels.hubLoading;
export const selectDuelsHubErrorKey = (s: WithDuels) => s.duels.hubErrorKey;
export const selectDuelResult = (duelId: string) => (s: WithDuels) =>
	s.duels.results[duelId] ?? null;
export const selectDuelResultErrorKey = (duelId: string) => (s: WithDuels) =>
	s.duels.resultErrorKeys[duelId];
export const selectDuelsHistory = (s: WithDuels) => s.duels.history;
export const selectDuelSeasons = (s: WithDuels) => s.duels.seasons;
export const selectDuelSeasonProgress = (s: WithDuels) => s.duels.season;
export const selectDuelSeasonLoading = (s: WithDuels) => s.duels.seasonLoading;
export const selectDuelSeasonErrorKey = (s: WithDuels) => s.duels.seasonErrorKey;
export const selectDuelRewardsLoaded = (s: WithDuels) => s.duels.rewards !== null;
export const selectDuelRewardsLoading = (s: WithDuels) => s.duels.rewardsLoading;
export const selectDuelRewardsErrorKey = (s: WithDuels) => s.duels.rewardsErrorKey;
export const selectDuelRewardTransition = (s: WithDuels) => s.duels.transition;
export const selectDuelRewardOverlays = (s: WithDuels) => s.duels.rewardOverlays;
export const selectDuelClaimingRewardIds = (s: WithDuels) => s.duels.claimingRewardIds;
export const selectDuelClaimErrorKey = (s: WithDuels) => s.duels.claimErrorKey;
export const selectDuelRanking = (scope: DuelRankingScope) => (s: WithDuels) =>
	s.duels.ranking[scope];

const selectRawRewards = (s: WithDuels) => s.duels.rewards;

export const selectDuelRewards = createSelector(
	[selectRawRewards, selectDuelRewardOverlays],
	(rewards, overlays) => (rewards ?? []).map(reward => withOverlay(reward, overlays[reward.id])),
);

export const selectDuelReward = (entitlementId: string) =>
	createSelector(
		[selectDuelRewards],
		rewards => rewards.find(reward => reward.id === entitlementId) ?? null,
	);

export const selectDuelRewardCounts = createSelector(
	[selectDuelRewards, selectRawRewards, selectDuelRewardOverlays, selectDuelsHub],
	(rewards, raw, overlays, hub) =>
		raw && Object.keys(overlays).length
			? {
					requestable: rewards.filter(
						reward => reward.rewardType === 'RealWorld' && reward.canRequest,
					).length,
					openRequests: rewards.filter(
						reward => reward.rewardType === 'RealWorld' && isOpenRequest(reward),
					).length,
					recentlyFulfilled: hub?.rewards.recentlyFulfilled ?? 0,
					claimable: rewards.filter(reward => reward.canClaim).length,
				}
			: (hub?.rewards ?? null),
);
